import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {DatePicker, Form, Input, InputNumber, Select, Switch} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {FormCard, spans, Hint} from 'pages/AssetManagement/v2/FormCard';
import {InstrumentActions} from 'pages/AssetManagement/v2/InstrumentActions';
import UploadImage from 'components/Startup/profile/companyImage';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {currencyCodes} from 'pages/adminOpportunities/stocksListings/create/CreateStock';
import {GET_ALL_COUNTRIES} from 'graphql/queries/startupCompanies';
import {isDev} from 'utils/constants';
import {UPDATE_STOCK_EXCHANGE} from './resolvers';
import styles from './exchanges.module.scss';

const BUCKET = isDev
  ? 'dabafinancialinstruments-nonprod'
  : 'dabafinancialinstruments-prod';

/**
 * Exchange identity.
 *
 * `code` is deliberately not an ordinary field. It is the stable natural key
 * that market data joins on — `(ticker, exchangeCode)` — and fund valuation
 * history is keyed on `(symbol, exchangeCode)`. Renaming it orphans both, so
 * it is read-only until explicitly unlocked and then confirmed.
 *
 * The update is a merge (verified against the gateway), so sending only these
 * fields leaves trading config and localised content untouched.
 */
export const ExchangeDetailsForm = ({exchange, refetch, canUpdate}) => {
  const [form] = Form.useForm();
  const [logo, setLogo] = useState(exchange.logoImage || null);
  const [preview, setPreview] = useState(exchange.logoImage || null);
  const [unlockCode, setUnlockCode] = useState(false);
  const [confirmRename, setConfirmRename] = useState(null);

  const {data: countryData} = useQuery(GET_ALL_COUNTRIES, {
    errorPolicy: 'all',
  });
  const countries = countryData?.getAllCountries ?? [];

  const [updateExchange, {loading}] = useMutation(UPDATE_STOCK_EXCHANGE, {
    errorPolicy: 'all',
  });

  useEffect(() => {
    form.setFieldsValue({
      stockExchangeName: exchange.stockExchangeName,
      code: exchange.code,
      website: exchange.website,
      CEO: exchange.CEO,
      headquarters: exchange.headquarters,
      baseCurrencyCode: exchange.baseCurrencyCode,
      numberOfListings: exchange.numberOfListings,
      stockPicksEnabled: Boolean(exchange.stockPicksEnabled),
      foundedDate: exchange.foundedDate ? moment(exchange.foundedDate) : null,
      countries: (exchange.countries ?? []).map(country => country.id),
      countriesOfOperation: (exchange.countriesOfOperation ?? []).map(
        country => country.id,
      ),
    });
    setLogo(exchange.logoImage || null);
    setPreview(exchange.logoImage || null);
    setUnlockCode(false);
  }, [exchange]);

  const buildInput = values => ({
    // NON_NULL on the input even though it is definitional, so it always goes.
    baseCurrencyCode: values.baseCurrencyCode,
    stockExchangeName: values.stockExchangeName,
    website: values.website || null,
    CEO: values.CEO || null,
    headquarters: values.headquarters || null,
    numberOfListings: values.numberOfListings ?? null,
    stockPicksEnabled: Boolean(values.stockPicksEnabled),
    ...(values.foundedDate && {
      foundedDate: moment(values.foundedDate).format(),
    }),
    ...(logo && {logoImage: logo}),
    ...(values.countries?.length && {countries: values.countries}),
    ...(values.countriesOfOperation?.length && {
      countriesOfOperation: values.countriesOfOperation,
    }),
    // Only ever sent when the admin unlocked the field and confirmed.
    ...(unlockCode && values.code !== exchange.code && {code: values.code}),
  });

  const submit = input =>
    updateExchange({variables: {id: exchange.id, input}})
      .then(({data}) => {
        const result = data?.updateStockExchange;
        if (result?.__typename === 'Error') return toast.error(result.message);
        toast.success('Exchange updated');
        setConfirmRename(null);
        setUnlockCode(false);
        refetch?.();
      })
      .catch(error => toast.error(error.message));

  const handleFinish = values => {
    if (!values.baseCurrencyCode) {
      return toast.error(
        'A base currency is required before this exchange can be saved',
      );
    }
    const input = buildInput(values);
    // Renaming the code is the one destructive edit on this screen.
    if (input.code) return setConfirmRename({input, next: input.code});
    return submit(input);
  };

  return (
    <div>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <FormCard
          title="Identity"
          description="How this exchange is named and branded across the platform">
          <div className={spans.full}>
            <UploadImage
              label="Upload Exchange Logo"
              isACL
              previewUrl={preview}
              setPreviewUrl={setPreview}
              setImgUrl={setLogo}
              folder="exchanges"
              bucket={BUCKET}
            />
          </div>
          <div className={spans.half}>
            <Form.Item
              name="stockExchangeName"
              label="Exchange name"
              rules={[{required: true, message: 'A name is required'}]}>
              <Input />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="code" label="Code">
              <Input disabled={!unlockCode} />
            </Form.Item>
            <Hint>
              The stable key market data joins on. An exchange row can be
              deleted and recreated — its id changes, this does not. Changing it
              orphans price history and fund valuations.
            </Hint>
            {/* Sits after the hint: `.hint` carries a negative top margin so it
                tucks under its field, and anything between the two is
                overlapped by it. */}
            {canUpdate && (
              <button
                type="button"
                className={styles.unlockButton}
                onClick={() => {
                  if (unlockCode) form.setFieldsValue({code: exchange.code});
                  setUnlockCode(current => !current);
                }}>
                {unlockCode ? 'Cancel code change' : 'Change code'}
              </button>
            )}
          </div>
        </FormCard>

        <FormCard
          title="Organisation"
          description="Who runs the exchange and from where">
          <div className={spans.half}>
            <Form.Item name="CEO" label="Chief executive">
              <Input />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="headquarters" label="Headquarters">
              <Input placeholder="Abidjan, Côte d’Ivoire" />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="website"
              label="Website"
              rules={[{type: 'url', message: 'Enter a valid URL'}]}>
              <Input placeholder="https://www.brvm.org" />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="foundedDate" label="Founded">
              <DatePicker className={styles.fullWidth} format="DD MMM YYYY" />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Market"
          description="Currency and reach. Currency is required — the exchange cannot be saved without one.">
          <div className={spans.half}>
            <Form.Item
              name="baseCurrencyCode"
              label="Base currency"
              rules={[
                {required: true, message: 'A base currency is required'},
              ]}>
              <Select showSearch placeholder="Select a currency">
                {currencyCodes.map(entry => (
                  <Select.Option key={entry.value} value={entry.value}>
                    {entry.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="numberOfListings" label="Number of listings">
              <InputNumber className={styles.fullWidth} min={0} />
            </Form.Item>
            <Hint>
              Stated by the exchange. Not the instrument count daba holds, which
              is shown on the header.
            </Hint>
          </div>
          <div className={spans.half}>
            <Form.Item name="countries" label="Countries">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label">
                {countries.map(country => (
                  <Select.Option
                    key={country.id}
                    value={country.id}
                    label={country.name}>
                    <span className={styles.countryOption}>
                      {country.icon && <img src={country.icon} alt="" />}
                      {country.name}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="countriesOfOperation"
              label="Countries of operation">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label">
                {countries.map(country => (
                  <Select.Option
                    key={country.id}
                    value={country.id}
                    label={country.name}>
                    <span className={styles.countryOption}>
                      {country.icon && <img src={country.icon} alt="" />}
                      {country.name}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Stock picks"
          description="Whether this exchange is a stock-pick market for Daba Pro">
          <div className={spans.half}>
            <Form.Item
              name="stockPicksEnabled"
              label="Stock picks enabled"
              valuePropName="checked">
              <Switch disabled={!canUpdate} />
            </Form.Item>
            <Hint>
              On: the exchange shows in the app&apos;s market list, picks
              editions can be published for it, and Pro plans can be sold for
              it. Off: it leaves the market list; its editions are kept.
            </Hint>
          </div>
        </FormCard>

        <InstrumentActions
          disabled={!canUpdate || loading}
          label={loading ? 'Saving…' : 'Save changes'}
        />
      </Form>

      <ConfirmModal
        openModal={Boolean(confirmRename)}
        setOpenModal={() => setConfirmRename(null)}
        loading={loading}
        title="Change the exchange code"
        confirmText={
          <span>
            Renaming <strong>{exchange.code}</strong> to{' '}
            <strong>{confirmRename?.next}</strong> breaks every join keyed on
            the old code: instrument price history, keyed on{' '}
            <em>(ticker, exchangeCode)</em>, and fund valuation history, keyed
            on <em>(symbol, exchangeCode)</em>. Existing records are not
            migrated. Continue?
          </span>
        }
        handleOperation={() => submit(confirmRename.input)}
      />
    </div>
  );
};

export default ExchangeDetailsForm;
